
import {
  getProfile,
  getAddressLookupData,
  getSuburb,
  bookJobSiteInspection,
  uploadJobFiles,
  createUserAddressBookItem,
} from '@/api';
import { isCancel } from 'axios';
import { sendError } from '@/helpers/sentry';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/button/Button.vue';
import FilesTable from '@/components/UI/filesTable/FilesTable.vue';
import UploadAttachments from '@/components/UI/UploadAttachments/UploadAttachments.vue';
import CustomerDetailsFieldGroup from '@/components/fieldGroups/CustomerDetailsFieldGroup.vue';
import * as Sentry from '@sentry/vue';
import * as routeNames from '@/router/routeNames';

import { clearPhoneNumber, convertTimeStringTo24Hours } from '@/utils/InputProcessingUtilities';
import { statesTimeZonesList } from '@/utils/PortalConstants';
import { getReferencesParamsWrapper } from '@/helpers/booking';

import { User, UserDataAdvancedJB } from '@/models/main';
import { JBAdvancedDeliveryDetailsStop, JBAdvancedStop, JBStopDates, UIStateJobBookingStop } from '@/models/jbAdvanced';
import { format, utcToZonedTime } from 'date-fns-tz';

import { Component, Vue } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { timeZoneDefault } from '@/utils/PortalConstants';

import { AxiosError } from 'axios';
import { AddressBookLookupData } from '@/models/addressBook';
import { addDays } from 'date-fns';

import LookupSuburb from '@/components/UI/lookups/LookupSuburb.vue';
import DateTimes from '@/components/jobBooking/DateTimes/DateTimes.vue';
import { notifyNetworkError, notifyNetworkSuccess } from '@/helpers/notifiers';

@Component({
  components: {
    FilesTable,
    BackButton,
    Button,
    LookupSuburb,
    DateTimes,
    UploadAttachments,
    CustomerDetailsFieldGroup,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class JobBookingSiteInspection extends Vue {
  public routeNames = routeNames;
  public loading = false;
  private user: User;
  public autocompleteSuburb = getSuburb;

  private userDataDefault: UserDataAdvancedJB = {
    customerCode: '',
    caller: '',
    phone_number: '',
    reference1: '',
    reference2: '',
    reference3: '',
    ref1_prompt: 'Reference 1',
    ref2_prompt: 'Reference 2',
    ref3_prompt: 'Reference 3',
    ref1_required: false,
    ref2_required: false,
    ref3_required: false,
  };
  public userData: UserDataAdvancedJB = { ...this.userDataDefault };

  private addresses: AddressBookLookupData[] = [];
  public addressesWithoutWarehousesNames: AddressBookLookupData[] = [];

  public stop: JBAdvancedDeliveryDetailsStop = {
    id: 0,
    suburb: '',
    state: '',
    postCode: '',
    notesPhone: '',
    notesInstruction: '',
    notesName: '',
    warehouse: false,
    suburbId: 0,
  };
  private stopDefaultData = { ...this.stop };

  public uiState: UIStateJobBookingStop = {
    id: 0,
    addressBookCheckbox: false,
  };

  private today = format(utcToZonedTime(new Date(), timeZoneDefault), 'yyyy-MM-dd'); // today
  private dateTimeDefaultData = {
    id: 0,
    date: this.today,
    timeIsSetted: false,
    timeFrom: '08:00 AM',
    timeTo: '04:00 PM',
    priority: false,
  };
  public dateTimes: JBStopDates[] = [{ ...this.dateTimeDefaultData }];
  public dateTime = '';
  private statesTimeZonesList = statesTimeZonesList;

  public files: File[] = [];

  public errorFromAPIResponse = '';

  public changeDates(dates): void {
    this.formChanged();
    this.dateTimes = dates;
  }

  public changeFiles(files: File[]): void {
    this.formChanged();
    this.files = [...this.files, ...files];
  }

  public deleteFile(fileIndex: number): void {
    this.files.splice(fileIndex, 1);
  }

  public changeSuburb(stop, item) {
    this.stop.suburb = item.suburb || item.name;
    this.stop.suburbId = item.suburbId || item.id;
    this.stop.postCode = item.postcode;
    this.stop.state = item.state;
  }

  public clearSuburb() {
    this.stop.suburb = '';
    this.stop.suburbId = 0;
    this.stop.postCode = '';
    this.stop.state = '';
  }

  public clearAddressBook() {
    this.stop.suburb = '';
    this.stop.suburbId = -1;
    this.stop.postCode = '';
    this.stop.state = '';
    this.stop.name = '';
    this.stop.address_1 = '';
    this.stop.address_2 = '';
    this.stop.notesName = '';
    this.stop.notesPhone = '';
    this.stop.notesInstruction = '';
    this.uiState.addressBookCheckbox = false;
  }

  public keyPressedddressBookName(key) {
    if (key.target.value) {
      this.stop.name = key.target.value.trim();
    }
  }

  public setCustomerCode(customerCode): void {
    if (customerCode) {
      this.userData.customerCode = customerCode;
      this.getAddressBookWrapper(customerCode);
    }
    getReferencesParamsWrapper(this.userData.customerCode, this.userData);
  }

  public formChanged(): void {
    if (this.errorFromAPIResponse.length > 0) {
      this.errorFromAPIResponse = '';
    }
  }

  public onlyNumberAndSpaceBlur() {
    this.userData.phone_number = clearPhoneNumber(this.userData.phone_number);
  }

  public onlyNumberAndSpace($event) {
    const keyCode = $event.keyCode ? $event.keyCode : $event.which;
    if ((keyCode < 48 || keyCode > 57) && keyCode !== 32 && keyCode !== 40 && keyCode !== 41) {
      $event.preventDefault();
    }
  }

  public changeAddressBook(address) {
    if (typeof address === 'string') {
      this.stop.name === address;
      address = {
        name: address,
      };
    }
    if (address && this.addresses.length >= 1) {
      const chosenAddress = this.addresses.filter(item => {
        return item.name.trim() === address.name.trim();
      });
      // name, address_1, address_2 – 30 symbols limit
      this.stop.name = address.name.trim().substring(0, 30);
      if (chosenAddress.length >= 1) {
        this.stop.name = chosenAddress[0].name.trim().substring(0, 30);
        this.stop.address_1 = chosenAddress[0].line1 ? chosenAddress[0].line1.substring(0, 30) : '';
        this.stop.address_2 = chosenAddress[0].line2 ? chosenAddress[0].line2.substring(0, 30) : '';
        this.stop.suburb = chosenAddress[0].suburb;
        this.stop.suburbId = chosenAddress[0].id;
        this.stop.notesName = chosenAddress[0].note_name ? chosenAddress[0].note_name : '';
        this.stop.notesPhone = chosenAddress[0].note_phone ? chosenAddress[0].note_phone : '';
        this.stop.notesInstruction = chosenAddress[0].note_text ? chosenAddress[0].note_text : '';
        this.stop.warehouse = chosenAddress[0].warehouse;
        const lookup = {
          suburb: chosenAddress[0].suburb.name,
          state: chosenAddress[0].suburb.state,
          postcode: chosenAddress[0].suburb.postcode,
          suburbId: chosenAddress[0].suburb.id,
        };
        this.changeSuburb(0, lookup);
        this.uiState.addressBookCheckbox = false;
      }
      this.$emit('changeStops', [this.stop]);
    }
  }

  // Clear all the form data and localStorage data
  public clearForm(): void {
    const form = this.$refs.form as HTMLFormElement;
    form.resetValidation();
    this.errorFromAPIResponse = '';

    const userTimeZone = this.user.timezone || timeZoneDefault;
    this.dateTime = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd');

    // for non-staff users only references are cleared
    if (this.$ability.can('staff', 'Job')) {
      this.userData = { ...this.userDataDefault };
    } else {
      this.userData.reference1 = '';
      this.userData.reference2 = '';
      this.userData.reference3 = '';
    }
    this.dateTimes = [{ ...this.dateTimeDefaultData }];
    this.clearAddressBook();
  }

  private async getAddressBookWrapper(code: string): Promise<void> {
    await getAddressLookupData(code)
      .then((res: AddressBookLookupData[]) => {
        this.addresses = res;
        this.addressesWithoutWarehousesNames = res.filter(x => !x.warehouse);
      })
      .catch(err => {
        Sentry.withScope(scope => {
          scope.setLevel('warning');
          Sentry.captureException(err);
        });
      });
  }

  /**
   * Helper function to generate JSON according to Job-web-schema JSON-schema.
   * @private
   */
  private buildJob() {
    const stopState = this.stop.state;
    const timeZone = stopState ? this.statesTimeZonesList[stopState].timezone : 'Australia/Sydney';
    const zoneString = format(utcToZonedTime(new Date(this.dateTime), timeZone), 'xxxxx', {
      timeZone,
    });
    const date = this.dateTime + 'T' + '07:00:00' + zoneString;

    const stopStop: JBAdvancedStop[] = [
      {
        time: {
          earliest: date,
          latest: date,
        },
        name: this.stop.name || '',
        suburb: this.stop.suburb.toString() || '0',
        state: this.stop.state,
        postcode: this.stop.postCode,
        address_line1: this.stop.address_1 || '',
        address_line2: this.stop.address_2 || '',
        number: 1,
        attributes: [],
        warehouse: false,
        note_text: this.stop.notesInstruction,
        note_name: this.stop.notesName,
        note_phone: this.stop.notesPhone,
        note_email: '',
      },
    ];

    if (this.dateTimes[0].timeIsSetted) {
      stopStop[0].time.earliest =
        this.dateTimes[0].date + 'T' + convertTimeStringTo24Hours(this.dateTimes[0].timeFrom) + zoneString;
      stopStop[0].time.latest =
        this.dateTimes[0].date + 'T' + convertTimeStringTo24Hours(this.dateTimes[0].timeTo) + zoneString;
    } else {
      const timeFrom = '09:00 AM';
      const timeTo = '05:00 PM';
      stopStop[0].time.earliest = this.dateTimes[0].date + 'T' + convertTimeStringTo24Hours(timeFrom) + zoneString;
      stopStop[0].time.latest = this.dateTimes[0].date + 'T' + convertTimeStringTo24Hours(timeTo) + zoneString;
    }

    const jobData = [
      {
        schema_version: '1.0',
        customer_code: this.userData.customerCode,
        caller: this.userData.caller,
        phone: this.userData.phone_number,
        source: 'WEB',
        REF1: this.userData.reference1,
        REF2: this.userData.reference2,
        REF3: this.userData.reference3,
        stops: stopStop,
        products: [],
      },
    ];
    return jobData;
  }

  /**
   * Book a job via API call
   * @private
   */

  private bookValidatedJob(): void {
    this.loading = true;
    const jobData = this.buildJob();

    bookJobSiteInspection(jobData)
      .then(res => {
        // files upload
        const params = {
          rh_id: res.data.booked[0].rh_id,
          rh_date: res.data.booked[0].rh_date,
          files: this.files,
        };
        if (this.files.length > 0) {
          uploadJobFiles(params).catch(() => {
            notifyNetworkError({ title: 'Attachments upload failed' });
          });
        }
        // save to address book
        if (this.uiState.addressBookCheckbox) {
          createUserAddressBookItem({
            suburb: this.stop.suburbId,
            name: this.stop.name || '',
            line1: this.stop.address_1 || '',
            line2: this.stop.address_2 || '',
            line3: '',
            city: '',
            note_name: this.stop.notesName,
            note_phone: this.stop.notesPhone,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            note_email: this.stop.notesEmail || '',
            note_text: this.stop.notesInstruction,
            warehouse: this.stop.warehouse,
          })
            .then(res => {
              if (res.status === 201) {
                notifyNetworkSuccess({ text: 'The address was added to the address book' });
              }
            })
            .catch(() => {
              notifyNetworkError({
                title: 'Error creating an address in the address book',
                text: 'You can add it manually on the Address Book page',
                duration: 3000,
              });
            });
        }

        // redirecting
        this.$router.push({
          path: `/detailed-job?job_id=${res.data.booked[0].rh_id}&job_date=${res.data.booked[0].rh_date}`,
        });
        this.$notify({
          group: 'network',
          clean: true,
        });
        notifyNetworkSuccess({ text: 'Job booked successfully' });
      })
      .catch((res: AxiosError | any) => {
        // HTTP 400 comes with error message
        if (res?.response?.status === 400 && res?.response?.data.failed.length > 0) {
          this.errorFromAPIResponse = res.response.data.failed[0].error_reason;
          Sentry.withScope(scope => {
            scope.setLevel('warning');
            Sentry.captureException(res.response?.data.failed[0].error_reason);
          });
          this.$nextTick(() => {
            const el = this.$el.querySelector('.error-response') as HTMLElement;
            if (el) {
              this.$vuetify.goTo(el, { offset: 100 });
            }
          });
        } else {
          notifyNetworkError({ title: 'Unknown error during job booking' });
          Sentry.withScope(scope => {
            scope.setLevel('error');
            Sentry.captureException(res);
          });
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public bookJob(): void {
    const form = this.$refs.form as HTMLFormElement;
    if (form.validate()) {
      this.bookValidatedJob();
    } else {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.v-messages.error--text:first-of-type') as HTMLElement;
        if (el) {
          this.$vuetify.goTo(el, { offset: 100 });
        }
      });
    }
  }

  private created(): void {
    const userTimeZone = this.user.timezone || timeZoneDefault;
    this.dateTime = format(utcToZonedTime(addDays(new Date(), 1), userTimeZone), 'yyyy-MM-dd'); // tomorrow
    this.today = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd'); // today
  }

  private async mounted(): Promise<void> {
    if (!this.$ability.can('staff', 'Job')) {
      this.userData.customerCode = this.user.customerCode;
      if (
        !this.user.customerCode ||
        !this.user.username ||
        !this.user.phone_number ||
        !this.user.ref1_prompt ||
        !this.user.ref2_prompt ||
        !this.user.ref3_prompt
      ) {
        try {
          await getProfile().then((res: User) => {
            // If user has first name put it into caller and slice to 16 as RH limit
            // else use username
            if (res.first_name) {
              this.userData.caller = (res.first_name + ' ' + res.last_name).slice(0, 16);
            } else {
              this.userData.caller = res.username.split('@')[0].slice(0, 16);
            }
            if (res.phone_number) {
              this.userData.phone_number = clearPhoneNumber(res.phone_number);
            }
          });
        } catch (err) {
          if (!isCancel(err)) {
            sendError(err, 'JobBookingSI');
          }
        }
      } else {
        this.userData.caller = this.user.username.split('@')[0].slice(0, 16);
        this.userData.phone_number = clearPhoneNumber(this.user.phone_number);
      }
    }

    if (this.user.customerCode) {
      await getReferencesParamsWrapper(this.user.customerCode, this.userData);
      await this.getAddressBookWrapper(this.user.customerCode);
    }
  }
}
